// Cliente para API Keycloak com OAuth2 e Auto-Refresh de Token
// Suporta dois realms: Generator e Musa (refresh sempre no mesmo realm que emitiu o token).
import { setLastRealm } from "@/config/auth";

// ========================================
// CONFIGURAÇÕES DE AUTENTICAÇÃO (via variáveis de ambiente)
// ========================================
const CONFIG = {
  baseUrl: import.meta.env.VITE_GENERATOR_API_URL,
  tokenUrl: import.meta.env.VITE_KEYCLOAK_TOKEN_URL,
  tokenUrlMusa: import.meta.env.VITE_KEYCLOAK_MUSA_TOKEN_URL,
  clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID || "portal-gerador",
};

export type KeycloakRealm = "generator" | "musa";

function getTokenUrlForRealm(realm: KeycloakRealm): string {
  return realm === "musa" ? CONFIG.tokenUrlMusa : CONFIG.tokenUrl;
}

/** Infere o realm a partir do claim iss do JWT (ex.: .../realms/musa ou .../realms/generator). */
export function getRealmFromIssuer(iss: string | undefined): KeycloakRealm {
  if (!iss || typeof iss !== "string") return "generator";
  const lower = iss.toLowerCase();
  if (lower.includes("/realms/musa")) return "musa";
  return "generator";
}

// ========================================
// GERENCIAMENTO DE ESTADO DO TOKEN (SessionStorage + Memória)
// ========================================
const TOKEN_STORAGE_KEY = "keycloak_tokens";

interface TokenData {
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
  /** Realm que emitiu o token; usado para refresh no endpoint correto (sem refresh cruzado). */
  realm?: KeycloakRealm;
}

// Cache em memória para performance
let _tokenCache: TokenData | null = null;

function decodeIssuer(token: string): string | undefined {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return undefined;
    const payload = JSON.parse(
      atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")),
    );
    return payload.iss;
  } catch {
    return undefined;
  }
}

// Função para carregar tokens do sessionStorage
function loadTokens(): TokenData | null {
  if (_tokenCache) return _tokenCache;

  const stored = sessionStorage.getItem(TOKEN_STORAGE_KEY);
  if (!stored) return null;

  try {
    const data = JSON.parse(stored) as TokenData;
    if (!data.accessToken) return null;
    if (!data.realm) {
      const iss = decodeIssuer(data.accessToken);
      data.realm = getRealmFromIssuer(iss);
    }
    _tokenCache = data;
    return _tokenCache;
  } catch {
    sessionStorage.removeItem(TOKEN_STORAGE_KEY);
    return null;
  }
}

// Função para salvar tokens no sessionStorage
function saveTokens(
  accessToken: string,
  refreshToken: string,
  expiresAt: number,
  realm?: KeycloakRealm,
): void {
  const resolvedRealm = realm ?? getRealmFromIssuer(decodeIssuer(accessToken));
  const tokenData: TokenData = {
    accessToken,
    refreshToken,
    expiresAt,
    realm: resolvedRealm,
  };
  _tokenCache = tokenData;
  sessionStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(tokenData));
  // Em qual realm a aba nova deve tentar a reautenticação silenciosa. Não
  // decide qual formulário mostrar — ver o comentário em config/auth.ts.
  setLastRealm(resolvedRealm);
}

// Função para limpar tokens
function clearTokensStorage(): void {
  _tokenCache = null;
  sessionStorage.removeItem(TOKEN_STORAGE_KEY);
}

// ========================================
// FUNÇÕES DE AUTENTICAÇÃO
// ========================================

/**
 * Garante um token válido antes de qualquer chamada.
 * Se estiver expirado, renova automaticamente usando refresh token.
 * Se não houver token, lança erro (usuário precisa fazer login via OAuth2).
 */
/** Exposto para uso pelos outros clients REST (agreements, mtr-manager, faturamento…) que precisam do Bearer token. */
export async function getValidToken(): Promise<string> {
  const tokens = loadTokens();
  const now = Date.now();

  // Se ainda temos um token e ele vale por mais 30 segundos, usamos ele
  if (tokens && now < tokens.expiresAt - 30000) {
    return tokens.accessToken;
  }

  // Se expirou mas temos o Refresh Token, renovar no mesmo realm (não há refresh cruzado)
  if (tokens?.refreshToken) {
    try {
      const realm =
        tokens.realm ?? getRealmFromIssuer(decodeIssuer(tokens.accessToken));
      const tokenUrl = getTokenUrlForRealm(realm);
      return await requestToken(
        "refresh_token",
        { refresh_token: tokens.refreshToken },
        tokenUrl,
        realm,
      );
    } catch (e) {
      clearTokensStorage();
      throw new Error("Sessão expirada. Por favor, faça login novamente.");
    }
  }

  // Se não há token, lançar erro - usuário precisa fazer login via OAuth2
  throw new Error("Usuário não autenticado. Por favor, faça login.");
}

/**
 * Faz a requisição de token ao Keycloak (endpoint do realm informado).
 * Para refresh, sempre usar o realm que emitiu o token (sem refresh cruzado).
 */
async function requestToken(
  grantType: string,
  params: Record<string, string>,
  tokenUrlOverride?: string,
  realmForSave?: KeycloakRealm,
): Promise<string> {
  const tokenUrl = tokenUrlOverride ?? CONFIG.tokenUrl;
  const body = new URLSearchParams({
    grant_type: grantType,
    client_id: CONFIG.clientId,
    ...params,
  });

  const response = await fetch(tokenUrl, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body,
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error("Erro na autenticação com Keycloak:", errorText);
    throw new Error("Falha na autenticação com Keycloak");
  }

  const data = await response.json();
  const expiresAt = Date.now() + data.expires_in * 1000;
  saveTokens(data.access_token, data.refresh_token, expiresAt, realmForSave);
  return data.access_token;
}

// ========================================
// INTERFACES DE TIPOS
// ========================================

interface KeycloakApiResponse<T> {
  pagination: {
    page: number;
    last_page: number;
    page_size: number;
    count: number;
  };
  results: T[];
}

interface KeycloakUser {
  id: number;
  deleted: boolean;
  created_at: string;
  updated_at: string;
  email: string;
  fullname: string | null;
  phone: string | null;
  role: string | null;
  keycloak_id: string | null;
  generator_id: number;
  generator_links?: Array<{
    generator_id: number;
    role: string;
    include_children: boolean;
    establishment_ids?: number[];
  }>;
}

interface FetchUsersParams {
  page?: number;
  pageSize?: number;
  nameFilter?: string;
  emailFilter?: string;
  generatorFilter?: string;
}

// ========================================
// PERFIS DE ACESSO (Keycloak Groups) — GR-637
// ========================================

interface UserProfilesResponse {
  profiles: string[];
}

interface ProfileOperationFailure {
  profile: string;
  action: "add" | "remove";
  error: string;
}

/** Erro das chamadas de perfis, com o status HTTP e a origem preservados. */
interface ProfilesError extends Error {
  httpStatus: number;
  /** `true` quando o corpo veio em JSON, ou seja, foi a API que respondeu. */
  fromApi: boolean;
  code?: string;
}

interface UserProfilesUpdateResponse {
  added: string[];
  removed: string[];
  /** Presente só quando o backend responde 207 (diff aplicado pela metade). */
  failed?: ProfileOperationFailure[];
  /** `true` quando a resposta veio como 207 Multi-Status. */
  partial?: boolean;
}

/**
 * Erro de chamada de perfis com o status HTTP preservado.
 *
 * O resto deste cliente embute o status na string da mensagem
 * (`fetchUserById`), o que obriga quem chama a fazer `includes("404")`. O
 * backend do GR-637 passou a distinguir 404 (usuário sem conta no Keycloak) de
 * 502 (Keycloak fora do ar) justamente para a tela poder dizer coisas
 * diferentes — jogar isso numa substring desperdiça a distinção.
 */
/**
 * Texto exibível para uma falha SEM corpo JSON — a API não explicou nada, então
 * quem explica é o cliente. Vai direto para a tela, então descreve o que
 * aconteceu, não o código do status.
 */
function describeProfilesFailure(status: number): string {
  if (status === 404) {
    return "A rota de perfis de acesso não existe nesta versão da API.";
  }
  if (status === 401 || status === 403) {
    return "Sem permissão para ler ou alterar os perfis deste usuário.";
  }
  if (status >= 500) {
    return "A API não conseguiu responder. Tente de novo em alguns instantes.";
  }
  return `A API respondeu ${status} sem detalhar o motivo.`;
}

async function buildProfilesError(
  response: Response,
  fallbackMessage: string,
): Promise<ProfilesError> {
  const errorText = await response.text();
  let message = describeProfilesFailure(response.status);
  let code: string | undefined;
  let fromApi = false;

  try {
    const parsed = JSON.parse(errorText);
    message = parsed.detail || parsed.message || parsed.error || message;
    code = parsed.code;
    fromApi = true;
  } catch {
    // Corpo não-JSON: não veio do DRF. Tipicamente a página HTML de 404 do
    // Django quando a rota nem existe (backend numa versão anterior, proxy
    // errado). Marcar isso importa: um 404 assim NÃO significa "usuário sem
    // conta no Keycloak", e afirmar isso na tela manda quem está depurando
    // procurar o problema no lugar errado.
    //
    // O corpo cru NÃO entra na mensagem: ela é exibida ao usuário, e despejar
    // uma página HTML inteira num aviso de tela é ilegível. O texto continua
    // disponível no console, que é onde ele serve para alguma coisa.
    console.error(`${fallbackMessage} — corpo não-JSON (${response.status}):`, errorText);
  }

  const error = new Error(message) as ProfilesError;
  error.httpStatus = response.status;
  error.fromApi = fromApi;
  if (code) error.code = code;
  return error;
}

// ========================================
// FUNÇÕES PÚBLICAS DE AUTENTICAÇÃO OAUTH2
// ========================================

/**
 * Troca o código de autorização OAuth2 por tokens de acesso.
 * realm: realm que emitiu o código (generator ou musa); define endpoint e redirect_uri e é salvo para refresh correto.
 */
export async function exchangeCodeForToken(
  code: string,
  realm: KeycloakRealm = "generator",
): Promise<void> {
  const tokenUrl = getTokenUrlForRealm(realm);
  const redirectPath =
    realm === "musa" ? "/login-redirect/musa" : "/login-redirect";
  const redirectUri =
    typeof window !== "undefined"
      ? `${window.location.origin}${redirectPath}`
      : import.meta.env.VITE_REDIRECT_URI ||
        `http://localhost:8080${redirectPath}`;
  const body = new URLSearchParams({
    grant_type: "authorization_code",
    code,
    redirect_uri: redirectUri,
    client_id: CONFIG.clientId,
  });

  const response = await fetch(tokenUrl, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Falha na autenticação com Keycloak: ${response.status} - ${errorText}`,
    );
  }

  const data = await response.json();
  const expiresAt = Date.now() + data.expires_in * 1000;
  saveTokens(data.access_token, data.refresh_token, expiresAt, realm);
}

/** Retorna o realm do token atual (para logout no realm correto). */
export function getStoredRealm(): KeycloakRealm {
  const tokens = loadTokens();
  if (!tokens?.accessToken) return "generator";
  return tokens.realm ?? getRealmFromIssuer(decodeIssuer(tokens.accessToken));
}

/**
 * Verifica se o usuário está autenticado (tem tokens válidos)
 */
export function isAuthenticated(): boolean {
  const tokens = loadTokens();
  if (!tokens) return false;

  const isValid = Date.now() < tokens.expiresAt;
  return isValid;
}

/**
 * Limpa os tokens de autenticação (logout)
 */
export function clearTokens(): void {
  clearTokensStorage();
}

/**
 * Retorna o refresh token armazenado (para revogação)
 */
export function getRefreshToken(): string | null {
  const tokens = loadTokens();
  return tokens?.refreshToken ?? null;
}

// ========================================
// API EXPORTADA
// ========================================

export const keycloakApi = {
  /**
   * Busca usuários da API Keycloak
   */
  async fetchUsers(
    params: FetchUsersParams = {},
  ): Promise<KeycloakApiResponse<KeycloakUser>> {
    const token = await getValidToken();

    const {
      page = 1,
      pageSize = 25,
      nameFilter,
      emailFilter,
      generatorFilter,
    } = params;

    // Construir query params
    const queryParams = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString(),
    });

    // Adicionar filtros se existirem
    if (nameFilter) {
      queryParams.append("fullname", nameFilter);
    }
    if (emailFilter) {
      queryParams.append("email", emailFilter);
    }
    if (generatorFilter) {
      queryParams.append("generator", generatorFilter);
    }

    const url = `${CONFIG.baseUrl}/users?${queryParams.toString()}`;

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Erro ao buscar usuários: ${response.status} - ${errorText}`,
        );
      }

      const data: KeycloakApiResponse<KeycloakUser> = await response.json();
      return data;
    } catch (error) {
      console.error("Erro ao buscar usuários do Keycloak:", error);
      throw error;
    }
  },

  /**
   * Busca um usuário específico por ID
   */
  async fetchUserById(userId: string | number): Promise<KeycloakUser> {
    const token = await getValidToken();
    const url = `${CONFIG.baseUrl}/users/${userId}`;

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Erro ao buscar usuário: ${response.status} - ${errorText}`,
        );
      }

      const data: KeycloakUser = await response.json();
      return data;
    } catch (error) {
      console.error("Erro ao buscar usuário do Keycloak:", error);
      throw error;
    }
  },

  /**
   * Consulta a situação de um e-mail antes de criar usuário (MC-91).
   * `user` vem preenchido em `deleted`/`incomplete` com registro anterior
   * (dados para reativação); `null` em `active` fora do escopo do ator e em
   * `incomplete` sem registro (conta de acesso órfã, sem dado prévio).
   */
  async lookupUserByEmail(
    email: string,
  ): Promise<{
    status: "available" | "active" | "deleted" | "incomplete";
    user: KeycloakUser | null;
  }> {
    const token = await getValidToken();
    const url = `${CONFIG.baseUrl}/users/lookup?email=${encodeURIComponent(email)}`;

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Erro ao consultar e-mail: ${response.status} - ${errorText}`,
        );
      }

      return await response.json();
    } catch (error) {
      console.error("Erro ao consultar e-mail no Keycloak:", error);
      throw error;
    }
  },

  /**
   * Cria um novo usuário na API
   * POST /users com vínculos em generator_links
   */
  async createUser(userData: {
    email: string;
    fullname: string;
    phone?: string;
    generator_links: Array<{
      generator_id: number;
      role: string;
      include_children: boolean;
      establishment_ids?: number[];
    }>;
    password: string;
  }): Promise<KeycloakUser> {
    const token = await getValidToken();
    const url = `${CONFIG.baseUrl}/users`;

    const requestBody: Record<string, any> = {
      email: userData.email,
      fullname: userData.fullname,
      phone: userData.phone || null,
      generator_links: userData.generator_links,
      password: userData.password,
    };

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `Erro ao criar usuário: ${response.status} - ${errorText}`;
        // MC-91: o backend passou a mandar `{ code, message, detail }`
        // estruturado — `code` é o contrato estável pro front decidir o
        // texto, `message` já vem em linguagem comum.
        let code: string | undefined;

        try {
          const errorData = JSON.parse(errorText);
          if (errorData.code) code = errorData.code;
          if (errorData.message || errorData.error || errorData.detail) {
            errorMessage =
              errorData.message || errorData.error || errorData.detail;
          }
        } catch {
          // Se não for JSON, usar o texto original
        }

        // Fallback por substring: só quando o backend não mandou `code`
        // estruturado (corpo não-JSON, infra fora do Django).
        if (!code) {
          const errorLower = errorMessage.toLowerCase();
          if (
            errorLower.includes("already exists") ||
            errorLower.includes("já existe") ||
            errorLower.includes("duplicate") ||
            response.status === 409
          ) {
            code = "user_email_already_exists";
            errorMessage = `Este email já está cadastrado na plataforma. Por favor, use outro email ou verifique se o usuário já existe na lista de usuários.`;
          }
        }

        // `httpStatus` alimenta o código de erro do analytics (GR-712, RN-05):
        // o texto de errorMessage nunca pode ir para o GA4 porque pode conter
        // o e-mail digitado devolvido pela API.
        const customError: any = new Error(errorMessage);
        customError.code = code;
        customError.httpStatus = response.status;
        customError.email = userData.email;
        throw customError;
      }

      const data: KeycloakUser = await response.json();
      return data;
    } catch (error) {
      console.error("Erro ao criar usuário no Keycloak:", error);
      throw error;
    }
  },

  /**
   * Atualiza um usuário na API (PATCH)
   */
  async updateUser(
    userId: string | number,
    userData: {
      fullname?: string;
      email?: string;
      phone?: string | null;
      role?: string;
      generator?: number;
      generator_id?: number;
      password?: string;
      generator_links?: Array<{
        generator_id: number;
        role: string;
        include_children: boolean;
        establishment_ids?: number[];
      }>;
    },
  ): Promise<KeycloakUser> {
    const token = await getValidToken();
    const url = `${CONFIG.baseUrl}/users/${userId}`;

    // Construir body apenas com os campos fornecidos
    const requestBody: Record<string, any> = {};
    if (userData.fullname !== undefined)
      requestBody.fullname = userData.fullname;
    if (userData.email !== undefined) requestBody.email = userData.email;
    if (userData.phone !== undefined)
      requestBody.phone = userData.phone || null;
    if (userData.role !== undefined) requestBody.role = userData.role;
    if (userData.generator_links !== undefined)
      requestBody.generator_links = userData.generator_links;
    // Backend espera generator_id (não generator) — usado quando não há generator_links
    if (userData.generator_links === undefined) {
      if (userData.generator_id !== undefined) {
        requestBody.generator_id = userData.generator_id;
      } else if (userData.generator !== undefined) {
        requestBody.generator_id = userData.generator;
      }
    }
    if (userData.password !== undefined)
      requestBody.password = userData.password;

    try {
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `Erro ao atualizar usuário: ${response.status} - ${errorText}`;

        // Tentar extrair mensagem de erro do JSON se disponível
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message || errorData.error || errorData.detail) {
            errorMessage =
              errorData.message || errorData.error || errorData.detail;
          }
        } catch {
          // Se não for JSON, usar o texto original
        }

        throw new Error(errorMessage);
      }

      const data: KeycloakUser = await response.json();
      return data;
    } catch (error: any) {
      console.error("Erro ao atualizar usuário no Keycloak:", error);
      throw error;
    }
  },

  /**
   * Deleta um usuário da API
   */
  async deleteUser(userId: string | number): Promise<void> {
    const token = await getValidToken();
    const url = `${CONFIG.baseUrl}/users/${userId}`;

    try {
      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `Erro ao deletar usuário: ${response.status} - ${errorText}`;

        // Tentar extrair mensagem de erro do JSON se disponível
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message || errorData.error || errorData.detail) {
            errorMessage =
              errorData.message || errorData.error || errorData.detail;
          }
        } catch {
          // Se não for JSON, usar o texto original
        }

        throw new Error(errorMessage);
      }

      // DELETE geralmente retorna 204 No Content ou 200 OK
      // Não precisa retornar nada se for sucesso
    } catch (error: any) {
      console.error("Erro ao deletar usuário no Keycloak:", error);
      throw error;
    }
  },

  /**
   * Perfis de acesso (Keycloak Groups) de um usuário — GR-637.
   * GET /users/{id}/profiles
   *
   * Devolve também grupos legados fora do catálogo (ex.: `Generator_3869`):
   * é acesso real e precisa aparecer na tela.
   */
  async fetchUserProfiles(userId: string | number): Promise<UserProfilesResponse> {
    const token = await getValidToken();
    const url = `${CONFIG.baseUrl}/users/${userId}/profiles`;

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw await buildProfilesError(response, "Erro ao buscar perfis do usuário");
      }

      const data: UserProfilesResponse = await response.json();
      return data;
    } catch (error) {
      console.error("Erro ao buscar perfis do usuário:", error);
      throw error;
    }
  },

  /**
   * Aplica um diff explícito de perfis — GR-637.
   * PUT /users/{id}/profiles  body: { add: [...], remove: [...] }
   *
   * O backend responde 207 quando parte do diff foi aplicada e parte não; o
   * corpo traz `failed` com o motivo de cada item. Não é erro de rede, então
   * não vira exceção — quem chama decide o que mostrar.
   */
  async updateUserProfiles(
    userId: string | number,
    diff: { add: string[]; remove: string[] },
  ): Promise<UserProfilesUpdateResponse> {
    const token = await getValidToken();
    const url = `${CONFIG.baseUrl}/users/${userId}/profiles`;

    try {
      const response = await fetch(url, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ add: diff.add, remove: diff.remove }),
      });

      if (!response.ok) {
        throw await buildProfilesError(response, "Erro ao atualizar perfis do usuário");
      }

      const data: UserProfilesUpdateResponse = await response.json();
      return { ...data, partial: response.status === 207 };
    } catch (error) {
      console.error("Erro ao atualizar perfis do usuário:", error);
      throw error;
    }
  },
};

export type {
  KeycloakUser,
  KeycloakApiResponse,
  UserProfilesResponse,
  UserProfilesUpdateResponse,
  ProfileOperationFailure,
  ProfilesError,
};
